// Resolved on use rather than captured in the constructor: the constructor runs
// once at upgrade - before the children exist if this element is defined ahead
// of parsing - and the modal's contents can be replaced afterwards by an A/B
// swap or a section re-render. Listeners are delegated on the element, so a
// replaced summary or close button is wired the moment it lands.
class DetailsModal extends HTMLElement {
  get detailsContainer() {
    return this.querySelector('details');
  }

  get summaryToggle() {
    return this.querySelector('summary');
  }

  connectedCallback() {
    // connectedCallback also fires on a plain move; the delegated listeners live
    // on this element and survive it, so bind them only once.
    if (this._bound) return;
    this._bound = true;

    this.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    ThemeBind.delegate(this, 'click', 'summary', this.onSummaryClick.bind(this));
    ThemeBind.delegate(this, 'click', 'button[type="button"]', () => this.close());

    this.markSummaryRole();
    // A swapped-in summary needs the role too.
    ThemeBind.onSwap(this, () => this.markSummaryRole());
  }

  markSummaryRole() {
    const summary = this.summaryToggle;
    // Guarded so it writes nothing when already correct - that is what keeps it
    // safe to call from the mutation observer above.
    if (summary && summary.getAttribute('role') !== 'button') summary.setAttribute('role', 'button');
  }

  isOpen() {
    const container = this.detailsContainer;
    return !!container && container.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    const details = event.target.closest('details');
    if (!details) return;
    details.hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.hasAttribute('data-modal-overlay')) this.close(false);
  }

  open(event) {
    const details = event.target.closest('details');
    if (!details) return;
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    details.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(details.querySelector('[tabindex="-1"]'), details.querySelector('input:not([type="hidden"])'));
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    const container = this.detailsContainer;
    if (container) container.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);
